import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import ProductCard from "@/components/product/ProductCard";
import WaitlistSignup from "@/components/product/WaitlistSignup";

const gbp = (n) => `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 0 })}`;

export default function ProductDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [related, setRelated] = useState([]);
    const [added, setAdded] = useState(false);
    const { add } = useCart();
    const { format, currency } = useCurrency();

    useEffect(() => {
        (async () => {
            try {
                const p = await api.get(`/products/${id}`);
                setProduct(p.data);
                const r = await api.get(`/products?category=${encodeURIComponent(p.data.category)}&limit=8`);
                setRelated(r.data.items.filter((x) => x.id !== p.data.id).slice(0, 4));
            } catch (e) {
                console.error(e);
            }
        })();
    }, [id]);

    if (!product) return <div className="loading">Loading piece…</div>;

    const sold = product.status === "sold";
    const canAdd = product.status === "live";

    const onAdd = () => {
        add(product);
        setAdded(true);
        setTimeout(() => setAdded(false), 1400);
    };

    return (
        <div className="pdp" data-testid="pdp-page">
            <div className="pdp-breadcrumb">
                <Link to="/shop">Shop</Link> · <Link to={`/shop?category=${encodeURIComponent(product.category)}`}>{product.category}</Link>
            </div>

            <div className="pdp-body">
                <div className="pdp-gallery" data-testid="pdp-gallery">
                    {(product.images || []).map((src, i) => (
                        <img key={i} src={src} alt={`${product.title} ${i + 1}`} loading={i === 0 ? "eager" : "lazy"} />
                    ))}
                    {(product.images || []).length === 0 && (
                        <div style={{ background: "var(--parchment-soft)", aspectRatio: "4/5" }} />
                    )}
                </div>

                <div className="pdp-info">
                    <div className="designer">{product.designer}</div>
                    <h1 data-testid="pdp-title">{product.title}</h1>

                    <div className="price" data-testid="pdp-price">
                        {product.original_price && product.original_price > product.price && (
                            <s>{gbp(product.original_price)}</s>
                        )}
                        {gbp(product.price)}
                    </div>
                    {currency !== "GBP" && format(product.price).reference && (
                        <div data-testid="pdp-price-ref" style={{
                            fontFamily: "var(--font-sans)", fontSize: 11, letterSpacing: "0.24em",
                            textTransform: "uppercase", color: "var(--cocoa)", opacity: 0.7, marginTop: 4,
                        }}>
                            {format(product.price).reference} · reference only, charged in GBP
                        </div>
                    )}

                    <div className="pdp-badges">
                        {product.authenticated && <span className="badge">Authenticated</span>}
                        <span className="badge">{product.condition}</span>
                        <span className="badge">1 of 1</span>
                    </div>

                    <p className="pdp-desc">{product.description}</p>

                    <div className="pdp-meta">
                        <div><span>Size</span><span>{product.size}</span></div>
                        {product.color && <div><span>Colour</span><span>{product.color}</span></div>}
                        <div><span>Condition</span><span>{product.new_with_tags ? "New with tags" : product.condition}</span></div>
                        {product.material && <div><span>Material</span><span>{product.material}</span></div>}
                        {product.hardware && <div><span>Hardware</span><span>{product.hardware}</span></div>}
                        {product.interior && <div><span>Interior</span><span>{product.interior}</span></div>}
                        {product.country_of_origin && <div><span>Made in</span><span>{product.country_of_origin}</span></div>}
                        {product.serial_number && <div><span>Serial</span><span>{product.serial_number}</span></div>}
                        {product.measurements && (
                            <div><span>Measurements</span><span>{product.measurements}</span></div>
                        )}
                        {product.accompanied_by?.length > 0 && (
                            <div><span>Accompanied by</span><span>{product.accompanied_by.join(", ")}</span></div>
                        )}
                    </div>

                    <button
                        className="btn"
                        onClick={onAdd}
                        disabled={!canAdd}
                        style={{ width: "100%", marginTop: 12 }}
                        data-testid="pdp-add-to-bag"
                    >
                        {sold ? "Sold" : added ? "Added ✓" : "Add to Bag"}
                    </button>

                    {!canAdd && <WaitlistSignup product={product} />}

                    <div className="pdp-accordion">
                        <details open>
                            <summary>Curation Note</summary>
                            <div className="content">
                                Reviewed against our brief: current, desirable, and worth calling a
                                friend about. Authenticated by our curators.
                            </div>
                        </details>
                        <details>
                            <summary>Shipping</summary>
                            <div className="content">
                                Flat-rate £12 shipping across the UK. International on request. Pieces
                                ship within 2 business days from London in RAX packaging.
                            </div>
                        </details>
                        <details>
                            <summary>Returns</summary>
                            <div className="content">
                                14-day returns on unworn pieces with tags intact. See{" "}
                                <Link to="/legal/returns" className="link-underline">Returns policy</Link>.
                            </div>
                        </details>
                    </div>
                </div>
            </div>

            {related.length > 0 && (
                <section className="section" style={{ padding: "80px 0 0" }}>
                    <div className="section-head">
                        <h2 className="h-section">Styled <em>with</em></h2>
                    </div>
                    <div className="product-grid">
                        {related.map((p) => (
                            <ProductCard key={p.id} product={p} />
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}
